/**
 * Tennis scoring validation utilities
 */

export interface SetScore {
  set_number: number;
  player1_games: number;
  player2_games: number;
  player1_tiebreak?: number;
  player2_tiebreak?: number;
}

export interface MatchResult {
  sets: SetScore[];
  winner_id: string;
}

/**
 * Validates a single set score according to tennis rules
 */
export function validateSetScore(score1: number, score2: number): string | null {
  // Both scores must be between 0 and 7
  if (score1 < 0 || score1 > 7 || score2 < 0 || score2 > 7) {
    return 'Scores must be between 0 and 7';
  }

  // Check for valid tennis scores
  const higher = Math.max(score1, score2);
  const lower = Math.min(score1, score2);

  // Normal set: winner must have 6+ games and be ahead by 2
  if (higher === 6 && lower <= 4) {
    return null; // Valid: 6-0, 6-1, 6-2, 6-3, 6-4
  }

  // Tiebreak set: 7-5 or 7-6
  if (higher === 7 && (lower === 5 || lower === 6)) {
    return null; // Valid
  }

  return `Invalid tennis score: ${score1}-${score2}. Winner must win by 2 games (6-4 or less) or win tiebreak (7-5, 7-6)`;
}

/**
 * Validates a complete match result
 */
export function validateMatchResult(sets: SetScore[]): string | null {
  // Must have at least 2 sets
  if (sets.length < 2) {
    return 'You must enter scores for at least Set 1 and Set 2';
  }

  // Validate each set
  for (let i = 0; i < sets.length; i++) {
    const set = sets[i];
    const error = validateSetScore(set.player1_games, set.player2_games);
    if (error) {
      return `Set ${i + 1}: ${error}`;
    }
  }

  // Determine winner by counting sets won
  let player1Sets = 0;
  let player2Sets = 0;

  sets.forEach((set) => {
    if (set.player1_games > set.player2_games) player1Sets++;
    else if (set.player2_games > set.player1_games) player2Sets++;
  });

  // Check for valid match result (one player must win 2 sets)
  if (player1Sets < 2 && player2Sets < 2) {
    return 'Invalid match result: One player must win at least 2 sets. Please enter Set 3 scores.';
  }

  return null;
}

/**
 * Determines the winner of a match based on sets
 */
export function determineWinner(sets: SetScore[], player1Id: string, player2Id: string): string {
  let player1Sets = 0;
  let player2Sets = 0;

  sets.forEach((set) => {
    if (set.player1_games > set.player2_games) player1Sets++;
    else if (set.player2_games > set.player1_games) player2Sets++;
  });

  return player1Sets > player2Sets ? player1Id : player2Id;
}
